import { APP_BASE_HREF } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ExtendedOnlineAccessInfo, OAuthCredential, OnlineAccessInfo, TempTokenData } from 'mbs-ui-shared@models';
import { ConfigurationService } from 'mbs-ui-shared@services';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthOnlineAccessService {
  public readonly tryRefreshToken$: Observable<boolean>;

  private readonly onlineAccessAuthUrlPrefix = 'api/auth/';
  private onlineAccessInfoInternal$: Observable<ExtendedOnlineAccessInfo>;

  constructor(protected http: HttpClient, protected config: ConfigurationService, @Inject(APP_BASE_HREF) private appBaseHref: string) {}

  // Configuration part

  public getOnlineAccessKey(): string {
    return this.config.get('onlineAccessKey');
  }

  public getOnlineAccessHref(): string {
    return this.config.get('onlineAccessHref');
  }

  public isMBSMode(): boolean {
    return !this.getOnlineAccessKey();
  }

  // Requests part

  public tempTokenRequest$(hid, data: { tokenTarget: string; computerHid?: string }): Observable<TempTokenData> {
    return this.http.post<TempTokenData>(`api/computers/${hid}/temptoken`, data);
  }

  public loginRequest$({ username, password }): Observable<OAuthCredential> {
    const request = (info: ExtendedOnlineAccessInfo): Observable<OAuthCredential> =>
      this.http.post<OAuthCredential>(`${this.onlineAccessAuthUrlPrefix}login`, {
        username,
        password,
        ...info.params
      });

    return this.onlineAccessInfo$().pipe(switchMap((info) => request(info)));
  }

  public logoutRequest$(refreshToken: string): Observable<boolean> {
    return this.http
      .post<OAuthCredential>(`${this.onlineAccessAuthUrlPrefix}logout`, {
        refreshToken
      })
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  public refreshOnlineAccessToken(refreshToken: string): Observable<OAuthCredential> {
    return this.http.post<OAuthCredential>(`${this.onlineAccessAuthUrlPrefix}login/refresh`, {
      refreshToken
    });
  }

  public onlineAccessInfoRequest$(params): Observable<OnlineAccessInfo> {
    return this.http.get<OnlineAccessInfo>(`${this.onlineAccessAuthUrlPrefix}info`, { params });
  }

  // Info internal part

  public onlineAccessInfo$(): Observable<ExtendedOnlineAccessInfo> {
    if (this.onlineAccessInfoInternal$) return this.onlineAccessInfoInternal$;

    const isLocalHostHostName = this.isLocalHost();
    const onlineAccessHref = isLocalHostHostName ? this.getOnlineAccessKey() : this.getOnlineAccessHref();

    if (!this.isLocalHost() && !this.isValidOnlineAccessHref(onlineAccessHref)) return EMPTY;

    const params = this.prepareInfoRequestParams(isLocalHostHostName, onlineAccessHref);

    this.onlineAccessInfoInternal$ = this.onlineAccessInfoRequest$(params).pipe(
      map((info) => ({ info, params })),
      shareReplay()
    );

    return this.onlineAccessInfoInternal$;
  }

  private isLocalHost(): boolean {
    return window.location.hostname.startsWith('localhost');
  }

  private prepareInfoRequestParams(isLocalHostHostName: boolean, onlineAccessHref: string): { alias?: string; customDns?: string } {
    let params = {};

    if (isLocalHostHostName) {
      const isAliasMode = !onlineAccessHref.startsWith('http');

      if (!isAliasMode) {
        onlineAccessHref = onlineAccessHref.replace('http://', '').replace('https://', '');
        onlineAccessHref = onlineAccessHref.endsWith('/') ? onlineAccessHref.slice(0, -1) : onlineAccessHref;
      }

      params = isAliasMode ? { alias: onlineAccessHref } : { customDns: onlineAccessHref };
    } else {
      const notRootFolder = (this.appBaseHref ?? '').split('/').length > 2;
      const href = `${location.origin}${location.pathname}`;
      const alias = href.startsWith(onlineAccessHref) ? href.replace(onlineAccessHref, '').split('/')[0] : '';
      const isAliasMode = notRootFolder && !!alias;

      params = isAliasMode ? { alias } : { customDns: window.location.hostname };
    }

    return params;
  }

  // Internal check configuration part

  private isValidOnlineAccessHref(href: string): boolean {
    if (!href) {
      console.log(`Incorrect Online Access configuration. Please check onlineAccessHref parameter`);
      return false;
    }
    if (!href.endsWith('/')) {
      console.log(`Incorrect Online Access configuration. Please add '/' at the end of onlineAccessHref parameter`);
      return false;
    }
    if (href.split('/').length < 5) {
      console.log(
        `Incorrect Online Access configuration. Please check onlineAccessHref parameter includes at least one folder after specifying the domain`
      );
      return false;
    }

    return true;
  }
}
